import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Card, CardContent, Chip, alpha, useTheme, Collapse, IconButton } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SbTextField from '@Components/Textfield';
import SBCountryDropdown from '@Components/Dropdown/Country';
import Contact from '@Components/Contact';
import GoogleMaps from '@Components/Location';
import { getFormattedLocation } from '@Services/formateLocation';
import { useFormik } from 'formik';
import { ExtraDetails, UserType } from '@Reducers/AdminShipment/Type';
import Schema from '../Schema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { updateShipment } from '@Reducers/AdminShipment';
import { useAiValidation } from '../../../hooks/useAiValidation';
import useScroll from '../useScroll';
import Actions from './Actions';
import { isActionAllowed } from '../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';

const initialValues = {
  phone: {
    telephoneCode: '',
    number: '',
  },
  alternatePhone: {
    name: '',
    telephoneCode: '',
    number: '',
  },
  name: '',
  email: '',
  companyName: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
  type: '',
  IdType: '',
  IdNumber: '',
};

function ShipperConsignee() {
  const { shipperConsigneeRef } = useScroll();
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);

  const _shipper = shipmentById?._shipperId;
  const _consignee = shipmentById?._consigneeId;
  const _shipperExtraDetail = shipmentById?.extraDetails?.shipper;
  const _consigneeExtraDetail = shipmentById?.extraDetails?.consignee;

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesNameObj);

  const isShipperConsigneeDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipper_or_consignee_details,
    PERFORM_ACTION.write,
  );

  const [showShipperAltContact, setShowShipperAltContact] = useState<boolean>(
    Boolean(_shipper?.alternatePhone?.name || _shipper?.alternatePhone?.number),
  );
  const [showConsigneeAltContact, setShowConsigneeAltContact] = useState<boolean>(
    Boolean(_consignee?.alternatePhone?.name || _consignee?.alternatePhone?.number),
  );

  const { validationStates, validate } = useAiValidation();

  const shipperFormik = useFormik({
    enableReinitialize: true,
    initialValues: _shipper ? { ..._shipper, ...(_shipperExtraDetail || {}) } : { ...initialValues },
    validationSchema: Schema.ShipperConsignee,
    onSubmit(values) {
      if (shipmentById?.shipmentNumber) {
        let extraDetails: ExtraDetails | undefined = undefined;
        if (values.type) {
          extraDetails = {
            shipper: {
              type: values.type,
              IdType: values?.IdType || '',
              IdNumber: values?.IdNumber || '',
            },
          };
        }
        dispatch(updateShipment({ shipmentNumber: shipmentById.shipmentNumber, shipper: values, extraDetails }));
      }
    },
  });

  const consigneeFormik = useFormik({
    enableReinitialize: true,
    initialValues: _consignee ? { ..._consignee, ...(_consigneeExtraDetail || {}) } : { ...initialValues },
    validationSchema: Schema.ShipperConsignee,
    onSubmit(values) {
      if (shipmentById?.shipmentNumber) {
        let extraDetails: ExtraDetails | undefined = undefined;
        if (values.type) {
          extraDetails = {
            consignee: {
              type: values.type,
              IdType: values?.IdType || '',
              IdNumber: values?.IdNumber || '',
            },
          };
        }
        dispatch(updateShipment({ shipmentNumber: shipmentById.shipmentNumber, consignee: values, extraDetails }));
      }
    },
  });

  // AI Validation for Shipper
  useEffect(() => {
    if (shipperFormik.values.email) {
      validate('shipper.email', 'email', shipperFormik.values.email);
    }
  }, [shipperFormik.values.email, validate]);

  useEffect(() => {
    const countryDisplay = countryHashById?.[shipperFormik.values.country]?.name || shipperFormik.values.country;
    validate('shipper.phoneNumber', 'phone', {
      country: countryDisplay || undefined,
      telephoneCode: shipperFormik.values.phone?.telephoneCode || undefined,
      number: shipperFormik.values.phone?.number || '',
    });
  }, [
    shipperFormik.values.phone?.number,
    shipperFormik.values.country,
    shipperFormik.values.phone?.telephoneCode,
    countryHashById,
    validate,
  ]);

  useEffect(() => {
    if (
      shipperFormik.values.country &&
      shipperFormik.values.pincode &&
      shipperFormik.values.city &&
      shipperFormik.values.state
    ) {
      const countryDisplay = countryHashById?.[shipperFormik.values.country]?.name || shipperFormik.values.country;
      validate('shipper.address', 'shipmentAddress', {
        pincode: shipperFormik.values.pincode,
        city: shipperFormik.values.city,
        state: shipperFormik.values.state,
        country: countryDisplay,
      });
    }
  }, [
    shipperFormik.values.country,
    shipperFormik.values.pincode,
    shipperFormik.values.city,
    shipperFormik.values.state,
    countryHashById,
    validate,
  ]);

  // AI Validation for Consignee
  useEffect(() => {
    if (consigneeFormik.values.email) {
      validate('consignee.email', 'email', consigneeFormik.values.email);
    }
  }, [consigneeFormik.values.email, validate]);

  useEffect(() => {
    const countryDisplay = countryHashById?.[consigneeFormik.values.country]?.name || consigneeFormik.values.country;
    validate('consignee.phoneNumber', 'phone', {
      country: countryDisplay || undefined,
      telephoneCode: consigneeFormik.values.phone?.telephoneCode || undefined,
      number: consigneeFormik.values.phone?.number || '',
    });
  }, [
    consigneeFormik.values.phone?.number,
    consigneeFormik.values.country,
    consigneeFormik.values.phone?.telephoneCode,
    countryHashById,
    validate,
  ]);

  useEffect(() => {
    if (
      consigneeFormik.values.country &&
      consigneeFormik.values.pincode &&
      consigneeFormik.values.city &&
      consigneeFormik.values.state
    ) {
      const countryDisplay = countryHashById?.[consigneeFormik.values.country]?.name || consigneeFormik.values.country;
      validate('consignee.address', 'shipmentAddress', {
        pincode: consigneeFormik.values.pincode,
        city: consigneeFormik.values.city,
        state: consigneeFormik.values.state,
        country: countryDisplay,
      });
    }
  }, [
    consigneeFormik.values.country,
    consigneeFormik.values.pincode,
    consigneeFormik.values.city,
    consigneeFormik.values.state,
    countryHashById,
    validate,
  ]);

  const handleShipperAddressSelect = (event: any) => {
    const address = getFormattedLocation(event.address_components);
    shipperFormik.setFieldValue('city', address.city);
    shipperFormik.setFieldValue('state', address.state);
    shipperFormik.setFieldValue('country', address.country.toLowerCase());
    shipperFormik.setFieldValue('pincode', address.pincode);
  };

  const handleConsigneeAddressSelect = (event: any) => {
    const address = getFormattedLocation(event.address_components);
    consigneeFormik.setFieldValue('city', address.city);
    consigneeFormik.setFieldValue('state', address.state);
    consigneeFormik.setFieldValue('country', address.country.toLowerCase());
    consigneeFormik.setFieldValue('pincode', address.pincode);
  };

  const shipperValues = shipperFormik.values;
  const shipperErrors = shipperFormik.errors;
  const consigneeValues = consigneeFormik.values;
  const consigneeErrors = consigneeFormik.errors;
  const shipperAltError = shipperErrors?.alternatePhone as UserType['alternatePhone'];
  const consigneeAltError = consigneeErrors?.alternatePhone as UserType['alternatePhone'];

  return (
    <Grid container spacing={3} ref={shipperConsigneeRef} sx={{ mt: 1, mb: 3, alignItems: 'stretch' }}>
      {/* ===================== SHIPPER CARD (ORIGIN) ===================== */}
      <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: '14px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Shipper Information
                </Typography>
                <Chip
                  label="Origin"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Sender details & dispatch location
              </Typography>
            </Box>
          </Box>

          {/* Form Body */}
          <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              {/* Name & Company */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Shipper Full Name"
                  name="name"
                  value={shipperValues.name}
                  onChange={shipperFormik.handleChange}
                  error={shipperErrors?.name}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Company Name"
                  name="companyName"
                  value={shipperValues.companyName}
                  onChange={shipperFormik.handleChange}
                  error={shipperErrors?.companyName}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Mobile & Email */}
              <Grid item xs={12} sm={6}>
                <Contact
                  phoneName="phone.number"
                  phoneNumber={shipperValues?.phone?.number || ''}
                  onChangePhoneNumber={shipperFormik.handleChange}
                  label="Mobile No. *"
                  telCountryCodeName="phone.telephoneCode"
                  telCountryCode={shipperValues?.phone?.telephoneCode || ''}
                  onChangeTelCountryCode={shipperFormik.handleChange}
                  error={shipperErrors?.phone?.number || shipperErrors?.phone?.telephoneCode}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['shipper.phoneNumber'] || validationStates['phoneNumber']}
                  onApplySuggestion={sug => shipperFormik.setFieldValue('phone.number', sug)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Email"
                  name="email"
                  value={shipperValues.email}
                  onChange={shipperFormik.handleChange}
                  error={shipperErrors?.email}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['shipper.email'] || validationStates['email']}
                  onApplySuggestion={sug => shipperFormik.setFieldValue('email', sug)}
                />
              </Grid>

              {/* Location Search */}
              <Grid item xs={12}>
                <GoogleMaps
                  onSelect={handleShipperAddressSelect}
                  label="Search Address Location"
                  placeholder="Start typing to search for location"
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Address Line 1 & Line 2 */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Address line 1"
                  name="line1"
                  value={shipperValues.line1}
                  error={shipperErrors?.line1}
                  onChange={shipperFormik.handleChange}
                  helperText={`${shipperValues.line1 || ''}`}
                  showCharCount={true}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Address line 2"
                  name="line2"
                  value={shipperValues.line2}
                  error={shipperErrors?.line2}
                  onChange={shipperFormik.handleChange}
                  showCharCount={true}
                  helperText={`${shipperValues.line2 || ''}`}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* City & State */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="City"
                  name="city"
                  value={shipperValues.city}
                  onChange={shipperFormik.handleChange}
                  error={shipperErrors?.city}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="State"
                  name="state"
                  value={shipperValues.state}
                  onChange={shipperFormik.handleChange}
                  error={shipperErrors?.state}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Country & Pincode */}
              <Grid item xs={12} sm={6}>
                <SBCountryDropdown
                  value={shipperValues.country?.toLowerCase() || ''}
                  onChange={([value]) => shipperFormik.setFieldValue('country', value?.toLowerCase())}
                  error={shipperErrors?.country}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Pincode"
                  name="pincode"
                  value={shipperValues.pincode}
                  error={shipperErrors?.pincode}
                  onChange={shipperFormik.handleChange}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['shipper.address']}
                  onApplySuggestion={sug => shipperFormik.setFieldValue('pincode', sug)}
                />
              </Grid>

              {/* Alternate Contact Toggle */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowShipperAltContact(!showShipperAltContact)}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}>
                    + Alternate Contact (Optional)
                  </Typography>
                  <IconButton size="small">
                    {showShipperAltContact ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={showShipperAltContact}>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <SbTextField
                        label="Alternate Contact Name"
                        name="alternatePhone.name"
                        value={shipperValues?.alternatePhone?.name || ''}
                        error={shipperAltError?.name}
                        onChange={shipperFormik.handleChange}
                        disabled={!isShipperConsigneeDetailsWriteAllowed}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Contact
                        phoneName="alternatePhone.number"
                        phoneNumber={shipperValues?.alternatePhone?.number || ''}
                        onChangePhoneNumber={e => {
                          shipperFormik.handleChange(e);
                          if (!shipperValues?.alternatePhone?.telephoneCode) {
                            shipperFormik.setFieldValue(
                              'alternatePhone.telephoneCode',
                              organizationCountry?.contactInfo?.telephoneCode?.toString() || '',
                            );
                          }
                        }}
                        label="Contact No"
                        telCountryCodeName="alternatePhone.telephoneCode"
                        telCountryCode={shipperValues?.alternatePhone?.telephoneCode || ''}
                        onChangeTelCountryCode={shipperFormik.handleChange}
                        error={shipperAltError?.telephoneCode || shipperAltError?.number}
                        disabled={!isShipperConsigneeDetailsWriteAllowed}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
            </Grid>

            {/* Actions */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Actions onSave={() => shipperFormik.handleSubmit()} disabled={!isShipperConsigneeDetailsWriteAllowed} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* ===================== CONSIGNEE CARD (DESTINATION) ===================== */}
      <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: '14px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.02),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.1),
                color: theme.palette.secondary.main || 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocationOnOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Consignee Information
                </Typography>
                <Chip
                  label="Destination"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.1),
                    color: theme.palette.secondary.main || 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Recipient details & delivery destination
              </Typography>
            </Box>
          </Box>

          {/* Form Body */}
          <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              {/* Name & Company */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Consignee Full Name"
                  name="name"
                  value={consigneeValues.name}
                  onChange={consigneeFormik.handleChange}
                  error={consigneeErrors?.name}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Company Name"
                  name="companyName"
                  value={consigneeValues.companyName}
                  onChange={consigneeFormik.handleChange}
                  error={consigneeErrors?.companyName}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Mobile & Email */}
              <Grid item xs={12} sm={6}>
                <Contact
                  phoneName="phone.number"
                  phoneNumber={consigneeValues?.phone?.number || ''}
                  onChangePhoneNumber={consigneeFormik.handleChange}
                  label="Mobile No. *"
                  telCountryCodeName="phone.telephoneCode"
                  telCountryCode={consigneeValues?.phone?.telephoneCode || ''}
                  onChangeTelCountryCode={consigneeFormik.handleChange}
                  error={consigneeErrors?.phone?.number || consigneeErrors?.phone?.telephoneCode}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['consignee.phoneNumber'] || validationStates['phoneNumber']}
                  onApplySuggestion={sug => consigneeFormik.setFieldValue('phone.number', sug)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Email"
                  name="email"
                  value={consigneeValues.email}
                  onChange={consigneeFormik.handleChange}
                  error={consigneeErrors?.email}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['consignee.email'] || validationStates['email']}
                  onApplySuggestion={sug => consigneeFormik.setFieldValue('email', sug)}
                />
              </Grid>

              {/* Location Search */}
              <Grid item xs={12}>
                <GoogleMaps
                  onSelect={handleConsigneeAddressSelect}
                  label="Search Address Location"
                  placeholder="Start typing to search for location"
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Address Line 1 & Line 2 */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Address line 1"
                  name="line1"
                  value={consigneeValues.line1}
                  error={consigneeErrors?.line1}
                  onChange={consigneeFormik.handleChange}
                  helperText={`${consigneeValues.line1 || ''}`}
                  showCharCount={true}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="Address line 2"
                  name="line2"
                  value={consigneeValues.line2}
                  error={consigneeErrors?.line2}
                  onChange={consigneeFormik.handleChange}
                  showCharCount={true}
                  helperText={`${consigneeValues.line2 || ''}`}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* City & State */}
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="City"
                  name="city"
                  value={consigneeValues.city}
                  onChange={consigneeFormik.handleChange}
                  error={consigneeErrors?.city}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  label="State"
                  name="state"
                  value={consigneeValues.state}
                  onChange={consigneeFormik.handleChange}
                  error={consigneeErrors?.state}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                />
              </Grid>

              {/* Country & Pincode */}
              <Grid item xs={12} sm={6}>
                <SBCountryDropdown
                  value={consigneeValues.country?.toLowerCase() || ''}
                  onChange={([value]) => consigneeFormik.setFieldValue('country', value?.toLowerCase())}
                  error={consigneeErrors?.country}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SbTextField
                  required
                  label="Pincode"
                  name="pincode"
                  value={consigneeValues.pincode}
                  error={consigneeErrors?.pincode}
                  onChange={consigneeFormik.handleChange}
                  disabled={!isShipperConsigneeDetailsWriteAllowed}
                  validationState={validationStates['consignee.address']}
                  onApplySuggestion={sug => consigneeFormik.setFieldValue('pincode', sug)}
                />
              </Grid>

              {/* Alternate Contact Toggle */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowConsigneeAltContact(!showConsigneeAltContact)}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}>
                    + Alternate Contact (Optional)
                  </Typography>
                  <IconButton size="small">
                    {showConsigneeAltContact ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={showConsigneeAltContact}>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <SbTextField
                        label="Alternate Contact Name"
                        name="alternatePhone.name"
                        value={consigneeValues?.alternatePhone?.name || ''}
                        error={consigneeAltError?.name}
                        onChange={consigneeFormik.handleChange}
                        disabled={!isShipperConsigneeDetailsWriteAllowed}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Contact
                        phoneName="alternatePhone.number"
                        phoneNumber={consigneeValues?.alternatePhone?.number || ''}
                        onChangePhoneNumber={e => {
                          consigneeFormik.handleChange(e);
                          if (!consigneeValues?.alternatePhone?.telephoneCode) {
                            consigneeFormik.setFieldValue(
                              'alternatePhone.telephoneCode',
                              organizationCountry?.contactInfo?.telephoneCode?.toString() || '',
                            );
                          }
                        }}
                        label="Contact No"
                        telCountryCodeName="alternatePhone.telephoneCode"
                        telCountryCode={consigneeValues?.alternatePhone?.telephoneCode || ''}
                        onChangeTelCountryCode={consigneeFormik.handleChange}
                        error={consigneeAltError?.telephoneCode || consigneeAltError?.number}
                        disabled={!isShipperConsigneeDetailsWriteAllowed}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
            </Grid>

            {/* Actions */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Actions
                onCancel={() => consigneeFormik.handleReset(_consignee ? { ..._consignee } : {})}
                onSave={() => consigneeFormik.handleSubmit()}
                disabled={!isShipperConsigneeDetailsWriteAllowed}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default ShipperConsignee;
